/**
 * QuickActionsWidget - Dashboard widget showing overdue items across all WHS modules
 * 
 * Displays a unified view of:
 * - Expired SDS (Dangerous Goods)
 * - Overdue services (Fire Safety)
 * - Low/Out of stock (Equipment)
 * - Pending regulator notifications (Major Incidents)
 * - Active welfare checks
 */
import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { AlertTriangle, Flame, Package, AlertOctagon, Heart, ChevronRight, RefreshCw, CheckCircle } from 'lucide-react';

// Module icon mapping
const MODULE_ICONS = {
  'dangerous-goods': AlertTriangle,
  'fire-safety': Flame,
  'equipment': Package,
  'major-incidents': AlertOctagon,
  'welfare': Heart,
};

// Module labels
const MODULE_LABELS = {
  'dangerous-goods': 'Dangerous Goods',
  'fire-safety': 'Fire Safety',
  'equipment': 'Equipment',
  'major-incidents': 'Major Incidents',
  'welfare': 'Welfare',
};

// Action type labels
const ACTION_TYPE_LABELS = {
  'SDS_EXPIRED': 'SDS Expired',
  'SERVICE_OVERDUE': 'Service Overdue',
  'LOW_STOCK': 'Low Stock',
  'OUT_OF_STOCK': 'Out of Stock',
  'PENDING_NOTIFICATION': 'Pending Notification',
  'WELFARE_ACTIVE': 'Active Check',
};

// Single action item
const ActionItem = ({ action, onNavigate }) => {
  const Icon = MODULE_ICONS[action.module] || AlertTriangle;
  const isCritical = action.severity === 'critical';
  
  return (
    <div 
      className={`quick-action-item ${isCritical ? 'critical' : 'warning'}`}
      onClick={() => onNavigate(action.module, action.item_id)}
      data-testid={`quick-action-${action.id}`}
    >
      <div className="quick-action-icon">
        <Icon size={18} />
      </div>
      <div className="quick-action-content">
        <span className="quick-action-title">{action.title}</span>
        <span className="quick-action-subtitle">{action.subtitle}</span>
      </div>
      <div className="quick-action-badge">
        {ACTION_TYPE_LABELS[action.type] || action.type}
      </div>
      <ChevronRight size={16} className="quick-action-arrow" />
    </div>
  );
};

// Summary badge
const SummaryBadge = ({ module, count, onNavigate }) => {
  const Icon = MODULE_ICONS[module] || AlertTriangle;
  const label = MODULE_LABELS[module] || module;
  
  if (count === 0) return null;
  
  return (
    <button 
      className="quick-action-summary-badge"
      onClick={() => onNavigate(module)}
      data-testid={`summary-badge-${module}`}
    >
      <Icon size={14} />
      <span className="badge-count">{count}</span>
      <span className="badge-label">{label}</span>
    </button>
  );
};

const QuickActionsWidget = ({ setCurrentPage }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/quick-actions');
      setData(res);
    } catch (err) {
      console.error('Failed to load quick actions:', err);
      setError('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleNavigate = (module, itemId) => {
    // Navigate to the appropriate page
    const pageMap = {
      'dangerous-goods': 'dg',
      'fire-safety': 'fire-safety',
      'equipment': 'equipment',
      'major-incidents': 'incidents',
      'welfare': 'welfare',
    };
    setCurrentPage(pageMap[module] || module);
  };

  // Loading state
  if (loading) {
    return (
      <div className="quick-actions-widget loading" data-testid="quick-actions-loading">
        <div className="quick-actions-header">
          <h3>Quick Actions</h3>
        </div>
        <div className="quick-actions-loading-state">
          <RefreshCw size={20} className="spinning" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="quick-actions-widget error" data-testid="quick-actions-error">
        <div className="quick-actions-header">
          <h3>Quick Actions</h3>
          <button className="refresh-btn" onClick={loadData}>
            <RefreshCw size={16} />
          </button>
        </div>
        <div className="quick-actions-error-state">
          <AlertTriangle size={20} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const { summary, actions } = data || { summary: { total: 0 }, actions: [] };
  const displayActions = expanded ? actions : actions.slice(0, 5);
  const hasMore = actions.length > 5;

  // All clear state
  if (summary.total === 0) {
    return (
      <div className="quick-actions-widget all-clear" data-testid="quick-actions-clear">
        <div className="quick-actions-header">
          <h3>Quick Actions</h3>
          <button className="refresh-btn" onClick={loadData} title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>
        <div className="quick-actions-clear-state">
          <CheckCircle size={32} />
          <span className="clear-title">All Clear</span>
          <span className="clear-subtitle">No items require attention</span>
        </div>
      </div>
    );
  }

  return (
    <div className="quick-actions-widget" data-testid="quick-actions-widget">
      <div className="quick-actions-header">
        <div className="header-left">
          <h3>Quick Actions</h3>
          <span className={`total-badge ${summary.critical > 0 ? 'critical' : 'warning'}`}>
            {summary.total} item{summary.total !== 1 ? 's' : ''}
          </span>
        </div>
        <button className="refresh-btn" onClick={loadData} title="Refresh">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Module summary badges */}
      <div className="quick-actions-summary">
        {Object.entries(summary.by_module || {}).map(([module, count]) => (
          <SummaryBadge 
            key={module} 
            module={module} 
            count={count} 
            onNavigate={handleNavigate}
          />
        ))}
      </div>

      {/* Action items list */}
      <div className="quick-actions-list">
        {displayActions.map(action => (
          <ActionItem 
            key={action.id} 
            action={action} 
            onNavigate={handleNavigate}
          />
        ))}
      </div>

      {/* Show more/less toggle */}
      {hasMore && (
        <button 
          className="quick-actions-toggle"
          onClick={() => setExpanded(!expanded)}
          data-testid="quick-actions-toggle"
        >
          {expanded ? 'Show Less' : `Show All (${actions.length})`}
        </button>
      )}
    </div>
  );
};

export default QuickActionsWidget;
